import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Monitor, ArrowRight } from "lucide-react";
import { Reveal, SectionHeading } from "@/components/Reveal";
import { useContent } from "@/context/ContentContext";

export default function Pricing() {
  const { content } = useContent();
  const navigate = useNavigate();
  const [perMonth, setPerMonth] = useState(false);

  if (!content) return <section id="plans" className="py-16 md:py-28" />;
  const plans = content.plans;

  return (
    <section id="plans" className="relative py-16 md:py-28" data-testid="pricing-section">
      <div className="absolute inset-0 section-fade" aria-hidden="true" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading
          overline="Subscription plans"
          title="Pick the plan that fits you"
          sub="Simple, honest pricing with every feature included. All plans come with setup assistance and 24/7 support."
        />

        <Reveal className="mb-12 flex justify-center" delay={0.1}>
          <div className="glass inline-flex rounded-full p-1.5" data-testid="pricing-toggle">
            <button
              data-testid="pricing-toggle-total-button"
              onClick={() => setPerMonth(false)}
              className={`rounded-full px-5 py-2 text-sm font-semibold transition-all ${!perMonth ? "bg-electric text-slate-900 shadow-[0_0_18px_rgba(0,240,255,0.35)]" : "text-slate-500 hover:text-slate-900"}`}
            >
              Total Price
            </button>
            <button
              data-testid="pricing-toggle-permonth-button"
              onClick={() => setPerMonth(true)}
              className={`rounded-full px-5 py-2 text-sm font-semibold transition-all ${perMonth ? "bg-electric text-slate-900 shadow-[0_0_18px_rgba(0,240,255,0.35)]" : "text-slate-500 hover:text-slate-900"}`}
            >
              Per Month
            </button>
          </div>
        </Reveal>

        <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-4">
          {plans.map((plan, i) => {
            const popular = !!plan.popular;
            return (
              <Reveal key={plan.id} delay={i * 0.08} className="h-full">
                <motion.div
                  whileHover={{ y: -6 }}
                  transition={{ type: "spring", stiffness: 300, damping: 22 }}
                  className={`glass relative flex h-full flex-col rounded-3xl p-7 ${popular ? "card-popular" : ""}`}
                  data-testid={`plan-card-${plan.id}`}
                >
                  {plan.badge && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full border border-electric/40 bg-white px-4 py-1 font-mono text-[10px] font-bold tracking-[0.2em] text-electric shadow-[0_0_16px_rgba(0,240,255,0.35)]">
                      {plan.badge}
                    </span>
                  )}
                  <h3 className="font-display text-xl font-bold text-slate-900">{plan.name}</h3>
                  <div className="mt-4 flex items-end gap-2">
                    <span className="font-display text-4xl font-extrabold tracking-tight text-slate-900" data-testid={`plan-${plan.id}-price`}>
                      ${perMonth && plan.months > 1 ? (plan.price / plan.months).toFixed(2) : Number(plan.price).toFixed(2)}
                    </span>
                    <span className="pb-1.5 text-xs text-slate-500">
                      {perMonth && plan.months > 1 ? "/month" : "one payment"}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-slate-500">
                    {perMonth && plan.months > 1 ? `billed as $${Number(plan.price).toFixed(2)} for ${plan.months} months` : `full ${plan.months > 1 ? `${plan.months} months` : "month"} of access`}
                  </p>

                  <ul className="mt-6 flex-1 space-y-3" data-testid={`plan-${plan.id}-features`}>
                    {(plan.features || []).map((f) => (
                      <li key={f} className="flex items-start gap-2.5 text-sm text-slate-600">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-electric" /> {f}
                      </li>
                    ))}
                  </ul>

                  <div className="mt-6 flex items-start gap-2 rounded-xl border border-slate-900/10 bg-slate-900/[0.03] px-4 py-3 text-[11px] leading-relaxed text-slate-500">
                    <Monitor className="mt-0.5 h-3.5 w-3.5 shrink-0 text-electric/70" />
                    <span data-testid={`plan-${plan.id}-devices`}>{plan.devices}</span>
                  </div>

                  <button
                    data-testid={`plan-${plan.id}-get-started-button`}
                    onClick={() => navigate(`/order?plan=${plan.id}`)}
                    className={`mt-6 inline-flex w-full items-center justify-center gap-2 rounded-2xl px-6 py-4 text-base ${popular ? "btn-primary" : "btn-ghost"}`}
                  >
                    Get Started <ArrowRight className="h-4 w-4" />
                  </button>
                </motion.div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
